// eslint-disable-next-line no-unused-vars
import isElectron from 'is-electron';

import Valid from '@/lib/Valid';
import * as generate from '@/lib/generate';
// eslint-disable-next-line import/no-cycle
import store from '@/store';

// * components * //
import TextInput from '@/components/form-inputs/TextInput';
import ShortTextInput from '@/components/form-inputs/ShortTextInput';
import Dropdown from '@/components/form-inputs/Dropdown';
import CustomDropdown from '@/components/form-inputs/CustomDropdown';
import MapPicker from '@/components/map/Picker';
import SearchForAsset from '@/components/form-inputs/SearchForAsset';
import SelectOrNew from '@/components/form-inputs/SelectOrNew';
import DateInput from '@/components/form-inputs/DateInput';
import CheckBox from '@/components/form-inputs/CheckBox';
import DropBox from '@/components/form-inputs/DropBox';
import AddText from '@/components/form-inputs/AddText';
import FormLabel from '@/components/form-inputs/FormLabel';
import NoDropBox from '@/components/form-inputs/NoDropBox';

import {
	GetActionRequestsDropdown,
	GetFirms,
	GetPipeTypes,
	GetReviewTypes,
	GetFunds,
	GetJobsTypes,
	GetJobsDropdown,
	GetNextJobNumber,
	GetNextAgreementNumber,
	GetFacilitiesDropdown,
	GetFacilitiesTypesDropdown,
	GetPermitsDropdown,
	GetNextRequestNumber,
	GetNextPermitNumber,
	GetAgreementCategories,
} from '@/lib/API';

import { JobStatuses } from '@/lib/assets/jobs';

/*
*    ! This is Important Information !
*    If you are looking to change a field's type, there are a few things that need to be considered.
*    You must consider that this field is in the backend validation so it needs to be updated with the field here,
*    Also you must consider what the default data is for that field which can be found here => src/lib/generate.js.
*/


const includeFileUpload = function ( children ) {
	if ( store.state.driveLinkState === 'valid' ) {
		children.push( 'files' );
	}
	else {
		children.push( 'noFiles' );
	}

	return children;
};

const user = ( obj = { edit : false } ) => ( {
	title : obj.edit ? 'Edit User' : 'New User',

	layout : [
		{
			title    : 'Create User',
			type     : 'section',
			children : [
				'name',
				'email',
				'permissionLevel',
			]
		}
	],

	fields : {

		name : {
			component : 'ShortTextInput',
			options   : {
				minimalLabel : 'Name',
				placeholder  : 'Name',
			},

			validate : Valid.string( {
				required : true,
				pattern  : /.+/,
			} ),
		},

		email : {
			component : 'ShortTextInput',
			options   : {
				minimalLabel : 'Email',
				placeholder  : 'Email',
			},

			validate : Valid.string( {
				required : true,
				pattern  : /.+@.+\.com/,
			} )
		},

		permissionLevel : {
			component : 'CustomDropdown',

			options : {
				minimalLabel : 'Permissions',
				placeholder  : 'Permissions',
				options      : [
					{
						text  : 'Read Only',
						value : 1,
					},
					{
						text  : 'Read and Write',
						value : 2,
					},
					{
						text  : 'Read, Write, and Admin',
						value : 3,
					}
				],
			},

			validate : Valid.selection,
		},

	},

	startingData : generate.user,

	components : {
		ShortTextInput,
		CustomDropdown,
	}
} );

const jobFields = ( type = 'job' ) => ( {

	name : {
		component : 'ShortTextInput',
		options   : {
			placeholder  : 'Project Name',
			minimalLabel : 'Project Name',
		},

		validate : Valid.string( {
			required : true,
			pattern  : /.+/,
		} ),
	},

	number : {
		component : 'ShortTextInput',
		options   : {
			label       : ( type === 'job' ) ? 'Job #' : 'Misc Job #',
			placeholder : type === 'job' ? '6137' : '2018-6137',
			prefill     : () => GetNextJobNumber( type ),
		},

		validate : Valid.string( {
			required : true,
			pattern  : type === 'job' ? /\d{4}/ : /\d{4}-\d{3}/,
		} ),
	},

	tractNumber : {
		component : 'ShortTextInput',
		options   : {
			label       : 'Tract #',
			placeholder : 'e.g. 2832',
		},

		validate : Valid.string( {
			required : false,
			pattern  : /.+/,
		} ),
	},

	manager : {
		component : 'ShortTextInput',

		options : {
			label       : 'Proj. Manager',
			placeholder : 'e.g. MJT',
		},

		validate : Valid.string( {
			required : true,
			pattern  : /^.{0,3}$/,
		} ),
	},

	fund : {
		component : 'SelectOrNew',

		options : {
			textInputProp : 'description',
			dropdownProp  : 'fundsKey',

			createNewLabel : 'Other',

			textInput : {
				minimalLabel : 'New Fund',
				placeholder  : 'New Fund',
			},

			dropdown : {
				minimalLabel      : 'Fund/Agreement',
				options           : GetFunds,
				searchable        : true,
				searchPlaceholder : 'Fund/Agreement'
			}
		},

		validate : ( type === 'job' ) ? Valid.selectOrNew( 'fundsKey', 'description' ) : () => true,
	},

	expenseType : {
		component : 'CustomDropdown',

		options : {
			minimalLabel : 'Expense Type',
			options      : [
				{ text : 'Expense', value : 'Expense' },
				{ text : 'Capital', value : 'Capital' },
			],
			searchable        : true,
			searchPlaceholder : 'Expense Type',
		},

		default : 'Expense Type',

		validate : ( ...args ) => {
			if ( type === 'job' ) {
				return Valid.selection( ...args );
			}

			return true;
		}
	},

	jobsType : {
		component : 'SelectOrNew',

		options : {
			textInputProp : 'description',
			dropdownProp  : 'jobsTypesKey',

			createNewLabel : 'Other',

			textInput : {
				minimalLabel : 'Job Category',
				placeholder  : 'New Job Category',
			},

			dropdown : {
				minimalLabel      : 'Job Category',
				options           : GetJobsTypes,
				searchable        : true,
				searchPlaceholder : 'Job Category',
			}
		},

		validate : Valid.selectOrNew( 'jobsTypesKey', 'description' ),
	},

	status : {
		component : 'CustomDropdown',

		options : {
			minimalLabel : 'Status',
			options      : JobStatuses
		},

		validate : Valid.selection,
	},

	category : {
		component : 'CustomDropdown',

		options : {
			minimalLabel : 'Category',
			options      : [
				{ text : 'Groundwater Sustainability', value : 'Groundwater Sustainability' },
				{ text : 'Surface Water Infrastructure', value : 'Surface Water Infrastructure' },
				{ text : 'Legal & Water Rights Defense', value : 'Legal & Water Rights Defense' },
				{ text : 'N/A', value : 'N/A' },
			],
			searchable        : true,
			searchPlaceholder : 'Category',
		},

		validate : arg => Valid.selection( arg, false ),
	},

	firm : {
		component : 'SelectOrNew',

		options : {
			createNewLabel : 'Create new firm',
			textInputProp  : 'description',
			dropdownProp   : 'firmsKey',

			textInput : {
				minimalLabel : 'Engineering Firm',
				placeholder  : 'Engineering Firm',
			},

			dropdown : {
				minimalLabel      : 'Engineering Firm',
				searchPlaceholder : 'Search for an engineering firm',
				optional          : true,
				options           : GetFirms,
				searchable        : true,
			}
		},

		validate : Valid.selectOrNew( 'firmsKey', 'description', false ),
	},

	projectFee : {
		component : 'ShortTextInput',
		options   : {
			minimalLabel    : 'Project Fees (Dev.)',
			placeholder     : 'Project Fees (Dev.)',
			beforeCharacter : '$',
		},

		validate : Valid.string( {
			required : false,
			pattern  : /[\d.]+/,
		} ),
	},

	fidCost : {
		component : 'ShortTextInput',
		options   : {
			minimalLabel    : 'FID Cost',
			placeholder     : 'FID Cost',
			subTitle        : 'Should include a 51% labor burden',
			beforeCharacter : '$',
		},

		validate : Valid.string( {
			required : false,
			pattern  : /[\d.]+/,
		} ),
	},

	facilities : {
		component : 'CustomDropdown',

		options : {
			minimalLabel : 'Facility',
			options      : () => GetFacilitiesDropdown().then( items => items.map( a => ( {
				text          : a.text,
				facilitiesKey : a.value
			} ) ) ),
			searchable        : true,
			multiselect       : true,
			searchPlaceholder : 'Select or search for a facility',
			keyProp           : 'facilitiesKey',
		},

		default : 'Select or search for a facility',

		validate : Valid.selection,
	},

	summary : {
		component : 'TextInput',
		options   : {
			minimalLabel : 'Project Summary',
			placeholder  : 'Project Summary',
			// maxCharacters : 125,
		},

		validate : Valid.string( {
			required : true,
			pattern  : /.+/,
		} ),
	},

	// ? getting rid of the location input from the form
	// location : {
	// 	component : 'MapPicker',

	// 	options : {
	// 		placeholder : 'e.g. "First and Herndon"'
	// 	},

	// 	validate : Valid.object( {
	// 		latitude : Valid.number( {
	// 			required : false
	// 		} ),
	// 		longitude : Valid.number( {
	// 			required : false
	// 		} ),
	// 		description : Valid.string( {
	// 			required : false
	// 		} ),
	// 	} ),
	// },

	locationDescription : {
		component : 'ShortTextInput',
		options   : {
			placeholder  : 'e.g. "First and Herndon"',
			minimalLabel : 'Cross streets',
		},
		validate : Valid.string( {
			required : false,
			pattern  : /.+/,
		} )
	},

	dateCreated : {
		component : 'DateInput',

		options : {
			typeDate     : true,
			minimalLabel : 'Date Job Created',
			defaultText  : 'Date Job Created',
		},

		validate : Valid.date( {
			required : true
		} ),
	},

	dateClosed : {
		component : 'DateInput',

		options : {
			typeDate     : true,
			minimalLabel : 'Date Job Closed',
			defaultText  : 'Date Job Closed',
		},

		validate : ( ...args ) => {
			const [val, vals] = args;
			const fullJob     = vals[1];

			const isClosed = ( fullJob.status === 'Closed' || fullJob.status === 'Complete' );

			if ( isClosed && !val ) {
				return 'Required when status closed';
			}

			return true;
		},
	},

	dateStarted : {
		component : 'DateInput',

		options : {
			typeDate     : true,
			minimalLabel : 'Construction Started',
			defaultText  : 'Construction Started',
		},

		validate : Valid.date( {
			required : false,
		} ),
	},

	dateEnded : {
		component : 'DateInput',

		options : {
			typeDate     : true,
			minimalLabel : 'Construction Closed',
			defaultText  : 'Construction Closed',
		},

		validate : Valid.date( {
			required : false
		} ),
	},

	actionRequests : {
		component : 'CustomDropdown',
		options   : {
			placeholder : 'Search for an action request',
			options     : () => GetActionRequestsDropdown().then( items => items.map( a => ( {
				text              : a.text,
				actionRequestsKey : a.value,
			} ) ) ),
			searchable        : true,
			multiselect       : true,
			searchPlaceholder : 'Search for an action request',
			keyProp           : 'actionRequestsKey',
			minimalLabel      : 'Action Requests',
		},

		validate : Valid.array( {
			minLength : 0,
		} ).items( Valid.object( {
			actionRequestsKey : Valid.number()
		} ) )
	},

	pipeLength : {
		component : 'ShortTextInput',
		options   : {
			label       : 'Pipe Length',
			placeholder : 'Pipe Length',
		},

		validate : Valid.string( {
			required : false,
		} ),
	},

	pipeDiameter : {
		component : 'ShortTextInput',
		options   : {
			label       : 'Pipe Diameter',
			placeholder : 'Pipe Diameter',
		},

		validate : Valid.string( {
			required : false,
		} ),
	},

	pipeType : {
		component : 'SelectOrNew',

		options : {
			createNewLabel : 'Other',
			textInputProp  : 'description',
			dropdownProp   : 'pipeTypesKey',

			textInput : {
				minimalLabel : 'Pipe Type',
				placeholder  : 'Pipe Type',
			},

			dropdown : {
				minimalLabel      : 'Pipe Type',
				options           : GetPipeTypes,
				searchable        : true,
				searchPlaceholder : 'Pipe Type',
			}
		},

		validate : type === 'job' ? Valid.selectOrNew( 'pipeTypesKey', 'description', false ) : () => true,
	},

	fidEstimate : {
		component : 'TextInput',
		options   : {
			minimalLabel  : 'FID Estimate',
			placeholder   : 'FID engineers estimate',
			maxCharacters : 20,
		},

		validate : Valid.string( {
			required : false,
			pattern  : /.+/,
		} ),
	},

	devEstimate : {
		component : 'TextInput',
		options   : {
			minimalLabel  : 'Dev. Estimate',
			placeholder   : 'Dev engineers estimate',
			maxCharacters : 20,
		},

		validate : Valid.string( {
			required : false,
			pattern  : /.+/,
		} ),
	},

	files : {
		component : 'DropBox',

		validate : Valid.array( {
			minLength : 0,
		} ).items( () => true )
	},

	noFiles : {
		component : 'NoDropBox',

		validate : () => true
	},

} );

const job = ( obj = { edit : false } ) => ( {
	title : obj.edit ? 'Edit Job' : 'New Job',

	className : ['jobs-form', 'left-align', 'highlight'],

	// augment layout to support all new features
	layout : [
		{
			type     : 'section',
			title    : 'Basic Information',
			children : [
				// first row
				'name',

				// second row
				[
					{
						field : 'number',
						style : {
							flex : '1 1 0',
						},
						className : 'highlight-text',
					},
					{
						field : 'tractNumber',
						style : {
							flex : '1.5 1.5 0',
						},
					},
					{
						field : 'manager',
						style : {
							flex : '2 2 0',
						},
					},
				],

				// third row
				[
					'fund',
					'expenseType',
				],

				// fourth row
				[
					{
						field : 'jobsType',
						style : {
							flex : '4 4 0',
						},
					},
					{
						field : 'status',
						style : {
							flex : '3 3 0',
						}
					}
				],

				// fifth row
				'category',

				// sixth row
				'firm',

				// seventh row
				[
					'projectFee',
					'fidCost',
				],

				// eighth row
				'facilities',

				// ninth row
				'summary',

				// tenth row
				// ? getting rid of the location input from the form
				// 'location',
				'locationDescription',
			]
		},

		{
			type     : 'section',
			title    : 'Dates',
			children : [
				[
					'dateCreated',
					'dateClosed',
				],
				[
					'dateStarted',
					'dateEnded',
				],
			]
		},

		{
			type     : 'section',
			title    : 'Related Engineering <br> Action Requests',
			children : [
				'actionRequests',
			]
		},

		{
			type     : 'section',
			title    : 'Materials',
			children : [
				[
					'pipeLength',
					'pipeDiameter',
				],

				'pipeType',

				[
					'fidEstimate',
					'devEstimate',
				],
			]
		},
		{
			type     : 'section',
			title    : 'Files',
			children : includeFileUpload( [] ),
		}
	],

	fields : jobFields( 'job' ),

	startingData : generate.job,

	components : {
		TextInput,
		CustomDropdown,
		Dropdown,
		MapPicker,
		ShortTextInput,
		SelectOrNew,
		SearchForAsset,
		DateInput,
		DropBox,
		NoDropBox
	}
} );

const miscJob = ( obj = { edit : false } ) => ( {
	title : obj.edit ? 'Edit Misc. Job' : 'New Misc. Job',

	className : ['jobs-form', 'highlight'],

	// augment layout to support all new features
	layout : [
		{
			type     : 'section',
			title    : 'Basic Information',
			children : [
				// first row
				'name',

				// second row
				[
					{
						field : 'number',
						style : {
							flex : '2.5 2.5 0',
						},
					},
					{
						field : 'tractNumber',
						style : {
							flex : '2 2 0',
						},
					},
					{
						field : 'manager',
						style : {
							flex : '2.5 2.5 0',
						},
					},
				],

				// third row
				[
					{
						field : 'jobsType',
						style : {
							flex : '1.5 1.5 0',
						}
					},
					{
						field : 'status',
						style : {
							flex : '1 1 0',
						}
					}
				],

				// fourth row
				'category',

				// fifth row
				'firm',

				// sixth row
				'facilities',

				// seventh row
				'summary',

				// eighth row
				// ? getting rid of the location input from the form
				// 'location',
				'locationDescription',
			]
		},
		{
			type     : 'section',
			title    : 'Dates',
			children : [
				['dateCreated', 'dateClosed'],
			]
		},
		{
			type     : 'section',
			title    : 'Related Engineering<br>Action Requests',
			children : ['actionRequests'],
		},
		{
			type     : 'section',
			title    : 'Files',
			children : includeFileUpload( [] ),
		}
	],

	fields : jobFields( 'misc' ),

	startingData : generate.job,

	components : {
		TextInput,
		CustomDropdown,
		Dropdown,
		MapPicker,
		ShortTextInput,
		SelectOrNew,
		SearchForAsset,
		DateInput,
		DropBox,
		NoDropBox
	},
} );

const facility = ( obj = { edit : false } ) => ( {

	title : obj.edit ? 'Edit Facility' : 'New Facility',

	className : ['facilities-form', 'center-align', 'highlight'],

	layout : [
		{
			type     : 'section',
			title    : 'Facility Information',
			children : [
				'name',
				[
					'facilitiesType', //
					'number',
				],
				[
					'status', //
					'owner', //
				],
				'purchasedFromFclc',
				'deeded',
				'locationDescription',
				[
					'normalFlow',
					'maxFlow',
				],
				'comments',
			],
		}
	],

	fields : {

		name : {
			component : 'ShortTextInput',
			options   : {
				placeholder  : 'Facility Name',
				minimalLabel : 'Facility Name',
			},

			validate : Valid.string( {
				required : true,
				pattern  : /.+/,
			} ),
		},

		facilitiesType : {
			component : 'SelectOrNew',
			options   : {
				textInputProp : 'description',
				dropdownProp  : 'facilitiesTypesKey',

				createNewLabel : 'Other',

				textInput : {
					placeholder  : 'New Facility',
					minimalLabel : 'New Facility',
				},

				dropdown : {
					minimalLabel      : 'Facility Type',
					options           : GetFacilitiesTypesDropdown,
					// [
					// 	{
					// 		text  : 'Canal',
					// 		value : 0,
					// 	},
					// 	{
					// 		text  : 'Pipeline',
					// 		value : 1,
					// 	},
					// 	{
					// 		text  : 'River',
					// 		value : 2,
					// 	},
					// 	{
					// 		text  : 'Pond',
					// 		value : 3,
					// 	},
					// 	{
					// 		text  : 'Office',
					// 		value : 4,
					// 	},
					// 	{
					// 		text  : 'Pump Station',
					// 		value : 5,
					// 	},
					// 	{
					// 		text  : 'None',
					// 		value : 6,
					// 	},
					// ]
					// options      : GetFacilitiesDropdown,
					searchable        : true,
					searchPlaceholder : 'Facility Type'
				},
			},

			validate : Valid.selectOrNew( 'facilitiesTypesKey', 'description' ),
		},

		number : {
			component : 'ShortTextInput',
			options   : {
				placeholder  : 'Facility Number',
				minimalLabel : 'Facility Number',
			},

			validate : Valid.string( {
				required : true,
				pattern  : /\d+/,
			} ),
		},

		status : {
			component : 'CustomDropdown',

			options : {
				minimalLabel : 'Status',
				options      : [
					{
						text  : 'Active',
						value : 'Active',
					},
					{
						text  : 'Abandoned',
						value : 'Abandoned',
					},
				],
				searchable        : true,
				searchPlaceholder : 'Select or search for Status',
			},

			validate : Valid.selection,
		},

		owner : {
			component : 'CustomDropdown',

			options : {
				minimalLabel : 'Owner',
				options      : [
					{
						text  : 'FID',
						value : 'FID',
					},
					{
						text  : 'Private',
						value : 'Private',
					},
				],
				searchable        : true,
				searchPlaceholder : 'Select or search for Owner',
			},

			validate : Valid.selection,
		},

		purchasedFromFclc : {
			component : 'CheckBox',

			options : {
				title   : 'Purchased from Fresno Canal & Land Co.',
				bubbled : false,
			},

			validate : () => true,
		},

		deeded : {
			component : 'CheckBox',

			options : {
				title   : 'Deeded to FID',
				bubbled : false,
			},

			validate : () => true,
		},

		locationDescription : {
			component : 'ShortTextInput',
			options   : {
				placeholder  : 'Cross streets',
				minimalLabel : 'Location of Facility Head',
			},

			validate : Valid.string( {
				required : false,
				pattern  : /.+/,
			} )
		},

		normalFlow : {
			component : 'ShortTextInput',
			options   : {
				placeholder  : 'Normal Capacity (CFS)',
				minimalLabel : 'Normal Capacity (CFS)',
			},

			validate : Valid.number( {
				required : false,
			} )
		},

		maxFlow : {
			component : 'ShortTextInput',
			options   : {
				placeholder  : 'Max Capacity (CFS)',
				minimalLabel : 'Max Capacity (CFS)',
			},

			validate : Valid.number( {
				required : false,
			} )
		},

		comments : {
			component : 'TextInput',
			options   : {
				placeholder  : 'Comments',
				minimalLabel : 'Comments',
				// maxCharacters : 120,
			},

			validate : Valid.string( {
				required : false,
				pattern  : /.+/,
			} )
		},

	},

	startingData : generate.facility,

	components : {
		ShortTextInput,
		CustomDropdown,
		CheckBox,
		TextInput,
		SelectOrNew,
	}

} );

const agreements = ( obj = { edit : false } ) => ( {

	title : obj.edit ? 'Edit Agreement' : 'New Agreement',

	className : ['agreements-form', 'center-align', 'highlight'],

	layout : [
		{
			type     : 'section',
			title    : 'Basic Information',
			children : [
				'gisReference',
				'facilities',
				[
					'dateRecorded',
					'recordNumber',
				],
				[
					'recordOwner',
					'jobsKey',
				],
				[
					'agreementCategory',
					'type',
				]
			],
		},
		{
			type     : 'section',
			title    : 'Other Info',
			children : [
				[
					'vaultReference',
					{
						field : 'wasteWater',
						style : {
							flex : '0 0 auto',
						},
					},
					{
						field : 'mappedInGis',
						style : {
							flex : '0 0 auto',
						},
					},
				],
				[
					't',
					'r',
					's',
					'q',
				],
				'comments',
				// ? getting rid of the location input from the form
				// 'location',
				'locationDescription',
			],
		},
		{
			type     : 'section',
			title    : 'Files',
			children : includeFileUpload( [] ),
		}
	],

	fields : {

		gisReference : {
			component : 'FormLabel',

			options : {
				getValue : {
					type     : 'computed',
					generate : ( () => {
						let num = null;

						return ( ...args ) => {

							const { gisReference } = args[0];

							if ( num !== null ) {
								return num;
							}

							if ( gisReference ) {
								return gisReference;
							}

							return GetNextAgreementNumber()
								.then( ( data ) => {
									num = data.agreementNumber;

									return num;
								} );
						};
					} )(),
				},
				renderValue : agreementNumber => `<p>Map Reference #<span class='highlight-text'>: ${agreementNumber}</span></p>`,
				renderError : ( err ) => {

					switch ( err ) {

						case err.code === '500': {
							return 'Internal Server Error';
						}

						default: {
							return 'Could not load agreement number';
						}

					}

				}
			},

			validate : () => true,
		},

		facilities : {
			component : 'CustomDropdown',

			options : {
				options : () => GetFacilitiesDropdown().then( facilities => facilities.map( a => ( {
					text          : a.text,
					facilitiesKey : a.value,
				} ) ) ),
				searchable        : true,
				multiselect       : true,
				searchPlaceholder : 'Select or search for a facility',
				keyProp           : 'facilitiesKey',
				minimalLabel      : 'Facility',
			},

			default : 'Select or search for a facility',

			validate : Valid.selection,
		},

		dateRecorded : {
			component : 'DateInput',

			options : {
				typeDate     : true,
				minimalLabel : 'Recorded Date',
				defaultText  : 'Recorded Date',
			},

			validate : Valid.date( {
				required : true,
			} ),
		},

		recordNumber : {
			component : 'ShortTextInput',
			options   : {
				minimalLabel : 'Record Number',
				placeholder  : 'Record Number',
			},

			validate : Valid.number( {
				required : true,
			} ),
		},

		recordOwner : {
			component : 'ShortTextInput',
			options   : {
				minimalLabel : 'Record Owner',
				placeholder  : 'Record Owner',
			},

			validate : Valid.string( {
				required : true,
				pattern  : /.+/,
			} ),
		},

		jobsKey : {
			component : 'CustomDropdown',
			options   : {
				minimalLabel      : 'Job/Misc. Job #',
				options           : GetJobsDropdown,
				searchable        : true,
				searchPlaceholder : 'Job/Misc. Job #',
			},

			validate : Valid.number( {
				required : false,
			} ),
		},

		wasteWater : {
			component : 'CheckBox',

			options : {
				title   : 'Waste water?',
				bubbled : true,
			},

			validate : Valid.string( {
				required : false,
				pattern  : /.+/,
			} )
		},

		mappedInGis : {
			component : 'CheckBox',

			options : {
				title   : 'Mapped in GIS?',
				bubbled : true,
			},

			validate : () => true,
		},

		type : {
			component : 'ShortTextInput',

			options : {
				minimalLabel : 'Type',
				placeholder  : 'Agreement Type',
			},

			validate : Valid.string( {
				required : true
			} ),
		},

		vaultReference : {
			component : 'ShortTextInput',
			options   : {
				minimalLabel : 'Vault Ref',
				placeholder  : 'Vault Ref',
				// computedValue : ( form ) => {
				// 	const { number } = form;

				// 	if ( !number ) {
				// 		return '';
				// 	}

				// 	return number;
				// }
			},

			validate : Valid.number( {
				required : true,
			} ),
		},

		t : {
			component : 'ShortTextInput',
			options   : {
				label       : 'T',
				placeholder : 'e.g. 14',
			},

			validate : Valid.string( {
				required : false,
				pattern  : /.+/,
			} )
		},

		r : {
			component : 'ShortTextInput',
			options   : {
				label       : 'R',
				placeholder : 'e.g. 32',
			},

			validate : Valid.string( {
				required : false,
				pattern  : /.+/,
			} )
		},

		s : {
			component : 'ShortTextInput',
			options   : {
				label       : 'S',
				placeholder : 'e.g. 12',
			},

			validate : Valid.string( {
				required : false,
				pattern  : /.+/,
			} )
		},

		q : {
			component : 'ShortTextInput',
			options   : {
				label       : 'Q',
				placeholder : 'e.g. NW',
			},

			validate : Valid.string( {
				required : false,
				pattern  : /.+/,
			} )
		},

		comments : {
			component : 'TextInput',
			options   : {
				placeholder  : 'Additional comments',
				minimalLabel : 'Additional comments',
				// maxCharacters : 125,
			},

			validate : Valid.string( {
				required : false,
				pattern  : /.+/,
			} )
		},

		// ? getting rid of the location input from the form
		// location : {
		// 	component : 'MapPicker',

		// 	options : {
		// 		placeholder : 'e.g. "First and Herndon"'
		// 	},

		// 	validate : Valid.object( {
		// 		latitude : Valid.number( {
		// 			required : false
		// 		} ),
		// 		longitude : Valid.number( {
		// 			required : false
		// 		} ),
		// 		description : Valid.string( {
		// 			required : false
		// 		} ),
		// 	} ),
		// },

		locationDescription : {
			component : 'ShortTextInput',
			options   : {
				placeholder  : 'e.g. "First and Herndon"',
				minimalLabel : 'Cross streets',
			},

			validate : Valid.string( {
				required : false,
				pattern  : /.+/,
			} )
		},

		files : {
			component : 'DropBox',

			validate : Valid.array( {
				minLength : 0,
			} ).items( () => true )
		},

		noFiles : {
			component : 'NoDropBox',

			validate : () => true
		},

		agreementCategory : {
			component : 'SelectOrNew',

			options : {
				textInputProp : 'description',
				dropdownProp  : 'agreementCategoriesKey',

				createNewLabel : 'Create New Category',

				textInput : {
					minimalLabel : 'New Category',
					placeholder  : 'New Category',
				},

				dropdown : {
					minimalLabel      : 'Category',
					options           : GetAgreementCategories,
					searchable        : true,
					searchPlaceholder : 'Category',
				}
			},

			validate : Valid.selectOrNew( 'agreementCategoriesKey', 'description' ),
		},

	},

	startingData : generate.agreements,

	components : {
		FormLabel,
		ShortTextInput,
		DateInput,
		CustomDropdown,
		CheckBox,
		TextInput,
		MapPicker,
		DropBox,
		SelectOrNew,
		NoDropBox
	}

} );

const review = ( obj = { edit : false } ) => ( {
	title : obj.edit ? 'Edit Developer/Utility Review Reference' : 'New Developer/Utility Review Reference',

	className : ['review-form', 'center-align', 'highlight'],

	layout : [
		{
			type     : 'section',
			title    : 'Basic Information',
			children : [
				[
					'reviewsTypes',
					'referenceNumber',
				],
				'firm',
				'comments',
			],
		},
		{
			type     : 'section',
			title    : 'Dates',
			children : [
				[
					'dateReceived',
					'dateDeadline',
				],
				[
					'dateResponse',
					'responseBy',
				],
			],
		},
		{
			type     : 'section',
			title    : 'Location',
			children : [
				'facilities',
				'apn',
				// ? getting rid of the location input from the form
				// 'location',
				'locationDescription'
			],
		},
		{
			type     : 'section',
			title    : 'Files',
			children : includeFileUpload( [] ),
		}
	],

	fields : {

		reviewsTypes : {
			component : 'SelectOrNew',

			options : {
				textInputProp : 'description',
				dropdownProp  : 'reviewsTypesKey',

				createNewLabel : 'Other',

				textInput : {
					minimalLabel : 'Review Type',
					placeholder  : 'New Review Type',
				},

				dropdown : {
					minimalLabel : 'Review Type',
					options      : () => GetReviewTypes().then( types => types.map( ( { text, value } ) => ( {
						text,
						reviewsTypesKey : value,
					} ) ).sort( ( a, b ) => {

						const formattedA = a.text.toUpperCase();
						const formattedB = b.text.toUpperCase();

						if ( formattedA < formattedB ) {
							return -1;
						}

						if ( formattedA > formattedB ) {
							return 1;
						}

						// names must be equal
						return 0;

					} ) ),
					searchable  : true,
					multiselect : true,
				}
			},

			validate : Valid.array( {
				minLength : 1,
			} ).items( Valid.object( {
				reviewsTypesKey : Valid.string( { required : false } ),
				text            : Valid.string( { required : false } ),
			} ) ),
		},

		referenceNumber : {
			component : 'ShortTextInput',
			options   : {
				label       : 'Reference',
				placeholder : 'Reference',
			},

			validate : Valid.string( {
				required : true,
				pattern  : /.+/,
			} ),
		},

		firm : {
			component : 'SelectOrNew',

			options : {
				createNewLabel : 'Create new agency/engineer',
				textInputProp  : 'description',
				dropdownProp   : 'firmsKey',

				textInput : {
					minimalLabel : 'Agency/Engineer Name',
					placeholder  : 'Agency/Engineer Name',
				},

				dropdown : {
					minimalLabel      : 'Agency/Engineer',
					searchPlaceholder : 'Search for an agency/engineer',
					options           : GetFirms,
					searchable        : true,
				}
			},

			validate : Valid.selectOrNew( 'firmsKey', 'description' ),
		},

		comments : {
			component : 'TextInput',
			options   : {
				minimalLabel : 'Comments',
				placeholder  : 'Comments',
				// maxCharacters : 125,
			},

			validate : Valid.string( {
				required : false,
				pattern  : /.+/,
			} )
		},

		dateReceived : {
			component : 'DateInput',

			options : {
				typeDate     : true,
				minimalLabel : 'Date Received',
				defaultText  : 'Date Received',
			},

			validate : Valid.date( {
				required : true,
			} ),
		},

		dateDeadline : {
			component : 'DateInput',

			options : {
				typeDate     : true,
				minimalLabel : 'Deadline Date',
				defaultText  : 'Deadline Date',
			},

			validate : Valid.date( {
				required : true,
			} ),
		},

		dateResponse : {
			component : 'DateInput',

			options : {
				typeDate     : true,
				minimalLabel : 'Response Date',
				defaultText  : 'Response Date',
			},

			validate : Valid.date( {
				required : true,
			} ),
		},

		responseBy : {
			component : 'ShortTextInput',
			options   : {
				label       : 'Response By',
				placeholder : 'e.g. KHH',
			},

			validate : Valid.string( {
				required : true,
				pattern  : /.+/,
			} ),
		},

		facilities : {
			component : 'CustomDropdown',

			options : {
				minimalLabel : 'Facility',
				options      : () => GetFacilitiesDropdown().then( facilities => facilities.map( a => ( {
					text          : a.text,
					facilitiesKey : a.value,
				} ) ) ),
				keyProp           : 'facilitiesKey',
				searchable        : true,
				multiselect       : true,
				searchPlaceholder : 'Search for a facility',
			},

			validate : Valid.selection,
		},

		apn : {
			component : 'ShortTextInput',
			options   : {
				label       : '',
				placeholder : 'APN #',
			},

			validate : Valid.string( {
				required : false,
			} ),
		},

		// ? getting rid of the location input from the form
		// location : {
		// 	component : 'MapPicker',

		// 	options : {
		// 		placeholder : 'e.g. "First and Herndon"'
		// 	},

		// 	validate : Valid.object( {
		// 		latitude : Valid.number( {
		// 			required : false
		// 		} ),
		// 		longitude : Valid.number( {
		// 			required : false
		// 		} ),
		// 		description : Valid.string( {
		// 			required : false
		// 		} ),
		// 	} ),
		// },

		locationDescription : {
			component : 'ShortTextInput',
			options   : {
				placeholder  : 'e.g. "First and Herndon"',
				minimalLabel : 'Cross streets',
			},

			validate : Valid.string( {
				required : false,
				pattern  : /.+/,
			} )
		},

		files : {
			component : 'DropBox',

			validate : Valid.array( {
				minLength : 0,
			} ).items( () => true )
		},

		noFiles : {
			component : 'NoDropBox',

			validate : () => true
		},
	},

	startingData : generate.review,

	components : {
		ShortTextInput,
		SelectOrNew,
		TextInput,
		CustomDropdown,
		MapPicker,
		DateInput,
		DropBox,
		NoDropBox
	}

} );

const permit = ( obj = { edit : false } ) => ( {

	title : obj.edit ? 'Edit Permit' : 'New Permit',

	className : ['permit-form', 'center-align', 'highlight'],

	layout : [
		{
			type     : 'section',
			title    : 'Basic Information',
			children : [
				'number',
				[
					'permitsType',
					'jobsKey',
				],
				[
					{
						field : 'issuedBy',
						style : {
							flex : '0 0 39%',
						}
					},
					{
						field : 'permittee',
						style : {
							flex : '0 0 59%'
						}
					}
				],
				[
					'facilitiesKey',
					'status',
				],
				'summary',
			]
		},
		{
			type     : 'section',
			title    : 'Location',
			children : [
				// ? getting rid of the location input from the form
				// {
				// 	field     : 'location',
				// 	className : 'wide-search-bar',
				// 	style     : {
				// 		flex : '0 0 60%',
				// 	},
				// },
				{
					field : 'locationDescription'
				},
				{
					type     : 'section',
					title    : 'details',
					children : [
						'wso',
						[
							't',
							'r',
						],
						's',
						'q',
					],
				},
			]
		},
		{
			type     : 'section',
			title    : 'Dates',
			children : [
				[
					'dateIssued',
					'dateStarted',
					'dateFinished',
				],
				[
					'dateFinalInspection',
					'dateDepositReturned',
					'dateReminder',
				]
			]
		},
		{
			type     : 'section',
			title    : 'Fees',
			children : [
				[
					'permitFee',
					'inspectionFee',
					'materialsFee',
				],
				[
					'shutdownFee',
					'depositFee',
					'depositDeductions',
				],
			],
		},
		{
			type     : 'section',
			children : [
				'comments',
			],
		},
		{
			type     : 'section',
			title    : 'Files',
			children : includeFileUpload( [] ),
		}
	],

	fields : {

		number : {
			component : 'FormLabel',

			options : {
				watchProp : 'facilitiesKey',
				getValue  : {
					type     : 'computed',
					generate : async ( form, data ) => {
						let permitNumber;

						const getPermitNumber = async () => {
							if ( permitNumber ) {
								return permitNumber;
							}

							return Promise.resolve()
								.then( () => GetNextPermitNumber() )
								.then( ( number ) => {
									permitNumber = number;

									return number;
								} );
						};

						const prependWithZeros = ( str, minLength ) => {
							let edited = str;

							if ( typeof edited === 'number' ) {
								edited = edited.toString();
							}

							while ( edited.length < minLength ) {
								edited = `0${edited}`;
							}

							return edited;
						};

						const formattedPermit = async () => {
							const { number } = form;

							let permitNum;
							let permitYear;

							if ( number ) {
								const [num, year] = number.split( '-' );
								permitNum  = num;
								permitYear = year;
							}
							else {
								permitNum = await getPermitNumber( form );
								permitYear = new Date().getFullYear();
							}


							const facilityNum = ( () => {
								const { selectOptions } = data.facilitiesKey;
								const selectedOption    = selectOptions.find( a => a.value === form.facilitiesKey );

								if ( !selectedOption ) {
									return 'XXX';
								}

								return selectedOption.number;
							} )();

							return `${permitNum}-${permitYear}-${prependWithZeros( facilityNum, 3 )}`;
						};

						return formattedPermit();
					},
				},
				renderValue : permitNumber => `<p>Permit # ${permitNumber}</p><p>The permit number will complete once a canal is selected</p>`,
				renderError : ( err ) => {

					switch ( err ) {

						case err.code === '500': {
							return 'Internal Server Error';
						}

						default: {
							return 'Could not load permit number';
						}

					}

				}
			},

			validate : Valid.string( {
				required : false,
			} )
		},

		permitsType : {
			component : 'SelectOrNew',

			options : {
				textInputProp : 'description',
				dropdownProp  : 'permitsTypesKey',

				createNewLabel : 'Other',

				textInput : {
					minimalLabel : 'New Permit Type',
					placeholder  : 'New Permit Type',
				},

				dropdown : {
					minimalLabel : 'Permit Type',
					options      : GetPermitsDropdown,
				}
			},

			validate : Valid.selectOrNew( 'permitsTypesKey', 'description' ),
		},

		jobsKey : {
			component : 'CustomDropdown',
			options   : {
				minimalLabel      : 'Job #/Misc. Job #',
				options           : GetJobsDropdown,
				searchable        : true,
				searchPlaceholder : 'Job #/Misc. Job #',
			},

			validate : ( val ) => {

				if ( !val ) {
					return true;
				}

				return Valid.selection( val );

			},
		},

		issuedBy : {
			component : 'ShortTextInput',
			options   : {
				label       : 'Issued By',
				placeholder : 'e.g. MJT',
			},

			validate : Valid.string( {
				required : true,
				pattern  : /.+/,
			} ),
		},

		permittee : {
			component : 'ShortTextInput',
			options   : {
				minimalLabel : 'Permittee',
				placeholder  : 'Permittee',
			},

			validate : Valid.string( {
				required : true,
				pattern  : /.+/,
			} ),
		},

		facilitiesKey : {
			component : 'CustomDropdown',

			options : {
				minimalLabel      : 'Facility',
				options           : GetFacilitiesDropdown,
				searchable        : true,
				searchPlaceholder : 'Facility',
			},

			validate : Valid.selection,
		},

		status : {
			component : 'CustomDropdown',

			options : {
				minimalLabel      : 'Status',
				searchable        : true,
				searchPlaceholder : 'Status',
				options           : [
					{
						text  : 'Open',
						value : 'Open',
					},
					{
						text  : 'Closed',
						value : 'Closed',
					},
					{
						text  : 'Voided',
						value : 'Voided',
					},
				],
			},

			validate : ( ...args ) => {
				const [val, vals] = args;
				const fullPermit  = vals[1];

				const deductionsPresent = ( fullPermit.depositDeductions || fullPermit.depositDeductions === 0 );

				if ( !deductionsPresent && val === 'Closed' ) {
					return 'Must fill out deposit deductions';
				}

				return Valid.selection( ...args );
			},
		},

		summary : {
			component : 'TextInput',
			options   : {
				placeholder   : 'Work Summary',
				minimalLabel  : 'Work Summary',
				maxCharacters : 275,
			},

			validate : Valid.string( {
				required : true,
				pattern  : /.+/,
			} ),
		},

		// ? getting rid of the location input from the form
		// location : {
		// 	component : 'MapPicker',

		// 	options : {
		// 		placeholder : 'e.g. "First and Herndon"'
		// 	},

		// 	validate : Valid.object( {
		// 		latitude : Valid.number( {
		// 			required : false
		// 		} ),
		// 		longitude : Valid.number( {
		// 			required : false
		// 		} ),
		// 		description : Valid.string( {
		// 			required : false
		// 		} ),
		// 	} ),
		// },

		locationDescription : {
			component : 'ShortTextInput',
			options   : {
				placeholder  : 'e.g. "First and Herndon"',
				minimalLabel : 'Cross streets',
			},

			validate : Valid.string( {
				required : false,
				pattern  : /.+/,
			} )
		},

		wso : {
			component : 'CustomDropdown',

			options : {
				minimalLabel      : 'WSO Area',
				searchable        : true,
				searchPlaceholder : 'WSO Area',
				options           : ( () => {
					const opts = [];

					for ( let i = 101; i <= 112; i += 1 ) {

						opts.push( {
							text  : i,
							value : i,
						} );

					}

					return opts;
				} )(),
			},

			validate : () => true,
		},

		t : {
			component : 'ShortTextInput',
			options   : {
				label       : 'T',
				placeholder : 'e.g. 14',
			},

			validate : Valid.string( {
				required : false,
				pattern  : /.+/,
			} )
		},

		r : {
			component : 'ShortTextInput',
			options   : {
				label       : 'R',
				placeholder : 'e.g. 32',
			},

			validate : Valid.string( {
				required : false,
				pattern  : /.+/,
			} )
		},

		s : {
			component : 'ShortTextInput',
			options   : {
				label       : 'Sec',
				placeholder : 'e.g. 31',
			},

			validate : Valid.string( {
				required : false,
				pattern  : /.+/,
			} )
		},

		q : {
			component : 'ShortTextInput',
			options   : {
				label       : 'Q',
				placeholder : 'e.g. NW',
			},

			validate : Valid.string( {
				required : false,
				pattern  : /.+/,
			} )
		},

		dateIssued : {
			component : 'DateInput',

			options : {
				typeDate     : true,
				minimalLabel : 'Date Issued',
				defaultText  : 'Date Issued',
			},

			validate : Valid.date( {
				required : false,
			} ),
		},

		dateStarted : {
			component : 'DateInput',

			options : {
				typeDate     : true,
				minimalLabel : 'Start Date',
				defaultText  : 'Start Date',
			},

			validate : Valid.date( {
				required : false,
			} ),
		},

		dateFinished : {
			component : 'DateInput',

			options : {
				typeDate     : true,
				minimalLabel : 'Finish Date',
				defaultText  : 'Finish Date',
			},

			validate : Valid.date( {
				required : false,
			} ),
		},

		dateFinalInspection : {
			component : 'DateInput',

			options : {
				typeDate     : true,
				minimalLabel : 'Final Inspection',
				defaultText  : 'Final Inspection',
			},

			validate : Valid.date( {
				required : false,
			} ),
		},

		dateDepositReturned : {
			component : 'DateInput',

			options : {
				typeDate     : true,
				minimalLabel : 'Deposit Return',
				defaultText  : 'Deposit Return',
			},

			validate : Valid.date( {
				required : false,
			} ),
		},

		dateReminder : {
			component : 'DateInput',

			options : {
				typeDate     : true,
				minimalLabel : 'Reminder',
				defaultText  : 'Reminder',
			},

			validate : Valid.date( {
				required : false,
			} ),
		},

		permitFee : {
			component : 'ShortTextInput',
			options   : {
				minimalLabel    : 'Permit Fee',
				placeholder     : 'Permit Fee',
				beforeCharacter : '$',
			},

			validate : Valid.string( {
				required : true,
				pattern  : /[\d.]+/,
			} ),
		},

		inspectionFee : {
			component : 'ShortTextInput',
			options   : {
				minimalLabel    : 'Inspection Fee',
				placeholder     : 'Inspection Fee',
				beforeCharacter : '$',
			},

			validate : Valid.string( {
				required : true,
				pattern  : /[\d.]+/,
			} ),
		},

		materialsFee : {
			component : 'ShortTextInput',
			options   : {
				minimalLabel    : 'Material Fee',
				placeholder     : 'Material Fee',
				beforeCharacter : '$',
			},

			validate : Valid.string( {
				required : true,
				pattern  : /[\d.]+/,
			} ),
		},

		shutdownFee : {
			component : 'ShortTextInput',
			options   : {
				minimalLabel    : 'Shutdown Fee',
				placeholder     : 'Shutdown Fee',
				beforeCharacter : '$',
			},

			validate : Valid.string( {
				required : true,
				pattern  : /[\d.]+/,
			} ),
		},

		depositFee : {
			component : 'ShortTextInput',
			options   : {
				minimalLabel    : 'Deposit Fee',
				placeholder     : 'Deposit Fee',
				beforeCharacter : '$',
			},

			validate : Valid.string( {
				required : true,
				pattern  : /[\d.]+/,
			} ),
		},

		depositDeductions : {
			component : 'ShortTextInput',
			options   : {
				minimalLabel    : 'Deposit Deductions',
				placeholder     : 'Deposit Deductions',
				beforeCharacter : '$',
			},

			validate : Valid.string( {
				required : false,
				pattern  : /[\d.]+/,
			} )
		},

		comments : {
			component : 'AddText',

			options : {
				title         : 'Add Comment',
				label         : '',
				placeholder   : 'Add Comment',
				solidCircle   : true,
				singleComment : true,
			},

			validate : Valid.string( {
				required : false,
				pattern  : /.+/,
			} )
		},

		files : {
			component : 'DropBox',

			validate : Valid.array( {
				minLength : 0,
			} ).items( () => true )
		},

		noFiles : {
			component : 'NoDropBox',

			validate : () => true
		},
	},

	startingData : generate.permit,

	components : {
		FormLabel,
		SelectOrNew,
		CustomDropdown,
		ShortTextInput,
		TextInput,
		MapPicker,
		DateInput,
		AddText,
		DropBox,
		NoDropBox
	}

} );

const request = ( requestToUpdate = {} ) => ( {

	title : Object.keys( requestToUpdate ).length > 0 ? 'Edit Engineering Action Request Reference' : 'New Engineering Action Request Reference',

	className : ['request-form', 'center-align', 'highlight'],

	layout : [
		{
			type     : 'section',
			title    : 'Basic Information',
			children : [
				[
					{
						field     : 'number',
						className : 'request-number',
					},
					{
						field     : 'dateRequested',
						className : 'highlight-text',
					},
				],
				'requestingParty',
				'summary',
				[
					'jobsKey',
					'rulingAction',
				],
				'facilities',
				'fidContributions',
				// ? getting rid of the location input from the form
				// 'location',
				'locationDescription',
				'infoItem',
				'comments',
			]
		},
		{
			type     : 'section',
			title    : 'Files',
			children : includeFileUpload( [] ),
		}
	],

	fields : {
		number : {
			component : 'ShortTextInput',
			options   : {
				label       : 'Request #',
				placeholder : 'XXXX-XX',
				prefill     : async () => {
					if ( requestToUpdate.number ) {
						return requestToUpdate.number;
					}

					return `${new Date().getFullYear()}-${await GetNextRequestNumber()}`;
				},
			},

			validate : Valid.string( {
				required : false,
				pattern  : /.+/,
			} )
		},

		dateRequested : {
			component : 'DateInput',

			options : {
				typeDate     : true,
				minimalLabel : 'Request Date',
				defaultText  : 'Request Date',
			},

			validate : Valid.date( {
				required : true,
			} ),
		},

		requestingParty : {
			component : 'ShortTextInput',
			options   : {
				minimalLabel : 'Requesting Party',
				placeholder  : 'Requesting Party',
			},

			validate : Valid.string( {
				required : true,
			} ),
		},

		summary : {
			component : 'TextInput',
			options   : {
				placeholder  : 'Project Summary',
				minimalLabel : 'Project Summary',
				// maxCharacters : 125,
			},

			validate : Valid.string( {
				required : true,
				pattern  : /.+/,
			} ),
		},

		jobsKey : {
			component : 'CustomDropdown',
			options   : {
				minimalLabel      : 'Job/Misc. Job #',
				options           : GetJobsDropdown,
				searchable        : true,
				searchPlaceholder : 'Job/Misc. Job #',
			},

			validate : () => true,
		},

		rulingAction : {
			component : 'SelectOrNew',

			options : {
				textInputProp : 'description',
				dropdownProp  : 'rulingActionsKey',

				createNewLabel : 'Other',

				textInput : {
					minimalLabel : 'New Action',
					placeholder  : 'New Action',
				},

				dropdown : {
					minimalLabel : 'Ruling Action',
					options      : [
						{
							text  : 'Approved',
							value : 1,
						},
						{
							text  : 'Disapproved',
							value : 2,
						},
						{
							text  : 'No Action',
							value : 3,
						},
						{
							text  : 'Removed',
							value : 4,
						},
					],
					searchable        : true,
					searchPlaceholder : 'Ruling Action',
				}
			},

			validate : Valid.selectOrNew( 'rulingActionsKey', 'description' ),
		},

		facilities : {
			component : 'CustomDropdown',

			options : {
				minimalLabel : 'Facility',
				options      : () => GetFacilitiesDropdown().then( items => items.map( a => ( {
					text          : a.text,
					facilitiesKey : a.value,
				} ) ) ),
				searchable        : true,
				multiselect       : true,
				keyProp           : 'facilitiesKey',
				searchPlaceholder : 'Facility',
			},

			default : 'Facility',

			validate : Valid.selection,
		},

		fidContributions : {
			component : 'ShortTextInput',
			options   : {
				minimalLabel    : 'FID Contributions',
				placeholder     : 'FID Contributions',
				beforeCharacter : '$',
			},

			validate : Valid.string( {
				required : false,
			} ),
		},

		// ? getting rid of the location input from the form
		// location : {
		// 	component : 'MapPicker',

		// 	options : {
		// 		placeholder : 'e.g. "First and Herndon"'
		// 	},

		// 	validate : Valid.object( {
		// 		latitude : Valid.number( {
		// 			required : false
		// 		} ),
		// 		longitude : Valid.number( {
		// 			required : false
		// 		} ),
		// 		description : Valid.string( {
		// 			required : false
		// 		} ),
		// 	} ),
		// },

		locationDescription : {
			component : 'ShortTextInput',
			options   : {
				placeholder  : 'e.g. "First and Herndon"',
				minimalLabel : 'Cross streets',
			},

			validate : Valid.string( {
				required : false,
				pattern  : /.+/,
			} )
		},

		files : {
			component : 'DropBox',

			validate : Valid.array( {
				minLength : 0,
			} ).items( () => true )
		},

		noFiles : {
			component : 'NoDropBox',

			validate : () => true
		},

		infoItem : {
			component : 'CheckBox',

			options : {
				title   : 'Check for info item',
				bubbled : false,
			},

			validate : () => true,
		},

		comments : {
			component : 'AddText',

			options : {
				title         : 'Add Comment',
				label         : '',
				placeholder   : 'Add Comment',
				solidCircle   : true,
				singleComment : true,
			},

			validate : Valid.string( {
				required : false,
				pattern  : /.+/,
			} ),
		},
	},

	startingData : generate.request,

	components : {
		AddText,
		DateInput,
		SelectOrNew,
		ShortTextInput,
		CustomDropdown,
		TextInput,
		MapPicker,
		DropBox,
		CheckBox,
		NoDropBox
	}

} );

const forms = {
	user,
	job,
	miscJob,
	facility,
	agreements,
	review,
	permit,
	request,
};

export default forms;
